import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Activity, fieldValidation, LocationInspectionPointList, PublicAmorgUser, SafetyInspection, SafetyInspectionLocation } from '../../../amorg-common';
import { getActivity, updateActivity } from '../../../api/activityClientApi';
import { getLocations } from '../../../api/locationClientApi';
import { createAllLocationInspections, getAvailableLocationInspectionPoints, getSafetyInspection } from '../../../api/safetyInspectionClientApi';
import { getUsers } from '../../../api/userClientApi';
import { Button } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { CollapsiblePanel } from '../../../designSystem/collapsible/CollapsiblePanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../../designSystem/compositeComponents/ListRow';
import { ReferenceFilesPanelCollapsible } from '../../../designSystem/compositeComponents/ReferenceFilesPanel';
import { TitleContainer } from '../../../designSystem/compositeComponents/TitleContainer';
import { DeviceView } from '../../../designSystem/DeviceView';
import { Divider } from '../../../designSystem/Divider';
import { Icon } from '../../../designSystem/icons/Icons';
import { clientAccessHelper } from '../../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../_pageWrappers/MemberPageWrapper';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P } from '../../../designSystem/typography/Body';
import { H3 } from '../../../designSystem/typography/Heading';
import { ActivityHeader } from '../../_components/activities/ActivityHeader';
import { LocationPanel } from './_components/LocationPanel';
import { EditableTextArea } from '../../../designSystem/compositeComponents/EditableTextArea';
import { DateTimeStartEndFields } from '../../../designSystem/formField/DateTimeStartEndFields';
import { TextSaveField } from '../../../designSystem/formField/text/TextSaveField';
import { ActivityParticipantList } from '../../_components/participants/ActivityParticipantList';
import { Label } from '../../../designSystem/Label';
import { CountAvatar } from '../../../designSystem/avatar/CountAvatar';

export default function SafetyInspectionPage(): JSX.Element {
    const [activityTitle, setActivityTitle] = React.useState('');

    return (
        <MemberPageWrapper
            selectedMenuItemKey="safetyinspection"
            breadCrumbs={[
                {text:'Sikkerhedsrundering', url:'/safetyinspections'},
                ...(activityTitle ? [{text:activityTitle, url:''}] : [])
            ]}>
            <SafetyInspectionPageContent onActivityTitleChanged={setActivityTitle} />
        </MemberPageWrapper>
    );
}

type SafetyInspectionPageContentProps = {
    onActivityTitleChanged:(title:string) => void,
}

function SafetyInspectionPageContent(props:SafetyInspectionPageContentProps): JSX.Element
{
    const params = useParams<{ activityId: string }>();
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewActivity']);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return params.activityId 
        ? <SafetyInspectionPageView activityId={params.activityId} onActivityTitleChanged={props.onActivityTitleChanged} />
        : <span>ugyldig url</span>;
}

type SafetyInspectionPageViewProps =
    {
        activityId: string,
        onActivityTitleChanged:(title:string) => void,
    };
function SafetyInspectionPageView (props: SafetyInspectionPageViewProps): JSX.Element 
{
    const [inspection, setInspection] = React.useState<SafetyInspection | null>(null);
    const [availableInspections, setAvailableInspections] = React.useState<LocationInspectionPointList[] | null>(null);
    const [expandedLocationIds, setExpandedLocationIds] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [activity, setActivity] = React.useState<Activity | null>(null);
    const [users, setUsers] = React.useState<PublicAmorgUser[]>([]);
    const [inspectionPointRedirect, setInspectionPointRedirect] = React.useState(false);

    React.useEffect(() =>
    {
        const load = async () =>
        {
            setLoading(true);
            const act = await getActivity(props.activityId);
            if(!act)
            {
                setLoading(false);
                return;
            }
            setActivity(act);
            props.onActivityTitleChanged(act.title);
            setUsers(await getUsers());
            setInspection(await getSafetyInspection(act.id));
            setAvailableInspections(await getAvailableLocationInspectionPoints());

            const locations = await getLocations();
            setExpandedLocationIds(locations.map(l => l.id));
            setInspection(await getSafetyInspection(props.activityId));
            setLoading(false);
        };
        load();
    }, [props.activityId]);


    async function handleClickCreateInspections (locationId: string)
    {
        if(!activity)
            return;

        setLoading(true);

        await createAllLocationInspections(activity.id, locationId);
        setInspection(await getSafetyInspection(activity.id));

        setLoading(false);
    }

    function handleToggleExpand (locationId: string, expanded: boolean)
    {
        setExpandedLocationIds(
            expanded
                ? [...expandedLocationIds, locationId]
                : expandedLocationIds.filter(id => id !== locationId)
        );
    }


    async function performUpdate (updatedActivity: Activity)
    {
        //const a = await getActivity(initialActivity.id);
        const result = await updateActivity(
            updatedActivity.id,
            updatedActivity.title,
            updatedActivity.description,
            updatedActivity.start,
            updatedActivity.end,
            updatedActivity.location
        );

        handleUpdated();

        return result;
    }

    async function handleUpdated ()
    {
        setLoading(true);
        const act = await getActivity(props.activityId);
        if(!act)
        {
            setLoading(false);
            return;
        }   
        setActivity(act);
        props.onActivityTitleChanged(act.title);
        setUsers(await getUsers());
        setInspection(await getSafetyInspection(act.id));
        setAvailableInspections(await getAvailableLocationInspectionPoints());
        setLoading(false);
    }

    const locationsWithInspections = inspection?.locations.filter(l => l.facilities.some(f => f.inspectionPoints.length > 0)) || [];
    let availableLocations: SafetyInspectionLocation[] = [];
    if (availableInspections && inspection)
        availableLocations = inspection.locations.filter(l =>
            l.facilities.every(f => f.inspectionPoints.length === 0)
            && availableInspections.some(al => al.locationId === l.locationId && al.facilities.some(f => f.inspectionPoints.length > 0))
        ) || [];


    const sharedComponent = 
    activity &&
    <CollapsiblePanel
        defaultOpen={true}
        openHeader={
            <TitleContainer title={activity.title} updateTitle={(value) => performUpdate({...activity, title: value})} />
        }
        closedOrDefaultHeader={
            <TitleContainer title={activity.title} updateTitle={(value) => performUpdate({...activity, title: value})} />
    }>
        <Card variant="outlined">
            {activity &&

                <Flex flexDirection="column" gap={16}>
                    {activity &&
                        <>
                            <Flex flexDirection="row" gap={24}>

                                <Flex flexDirection="row" gap={24} alignItems="flex-start" width="100%">

                                    {(activity.activityType === "WorkgroupMeeting" || activity.activityType === "AnnualWorkgroupStatus") &&
                                        <Flex flexDirection="column" width="100%">
                                            <TextSaveField
                                                trailingIcon={"interface_glyph_location"}
                                                label={"Sted"}
                                                value={activity.location}
                                                onSave={newValue =>
                                                {
                                                    if (fieldValidation.activity.location(newValue))
                                                        performUpdate({...activity, location: newValue });
                                                }}
                                                validator={(currentValue) =>
                                                    fieldValidation.activity.location(currentValue)
                                                        ? { valid: true }
                                                        : { valid: false, errorMessage: 'Ugyldig beskrivelse. Maks. 200 tegn.' }
                                                }
                                            />
                                        </Flex>
                                    }

                                    <Flex flexDirection="row">
                                        <DateTimeStartEndFields
                                            start={activity.start}
                                            end={activity.end}
                                            onChanged={(start, end) => performUpdate({...activity, start, end})}
                                            layoutDirection="row"
                                            hideLabels={false}
                                        />
                                    </Flex>
                                </Flex>

                            </Flex>

                            <Flex flexDirection="column">
                                <Flex gap={8} alignItems='center'>
                                    <Label>Ansvarlige</Label>
                                    <CountAvatar count={activity.participants.length} />
                                </Flex>
                                <ActivityParticipantList
                                    activity={activity}
                                    users={users}
                                    onParticipantsUpdated={handleUpdated} />
                            </Flex>

                            <EditableTextArea
                                ariaLabel="Beskrivelse"
                                placeholder="Beskrivelse..."
                                value={activity.description}
                                onSave={newValue =>
                                {
                                    if (fieldValidation.activity.description(newValue))
                                        performUpdate({...activity, description: newValue });
                                }}
                                validator={(currentValue) =>
                                    fieldValidation.activity.description(currentValue)
                                        ? { valid: true }
                                        : {
                                            valid: false, errorMessage: `Ugyldig beskrivelse. Maks. 10000 tegn (${currentValue.length}).`
                                        }}
                            />
                            
                        </>
                    }

                    <ReferenceFilesPanelCollapsible objectId={activity.id} referenceType="SafetyInspection" />

                </Flex>
            }
        </Card>
            
    </CollapsiblePanel>

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            {inspectionPointRedirect && activity &&
                <Navigate to={`/inspectionpoints?safetyInspectionId=${activity.id}/`} />
            }

            {activity &&
                <>
                    <ActivityHeader
                        activity={activity}
                        deleteConfirmationTextInModal="Tilhørende handlepunkter vil ligeledes blive slettet."
                        additionalToolBarButtons={[
                            { 
                                variant: 'text', 
                                icon:'navigation_settings', 
                                tooltip: 'Indstilling af kontrolpunkter', 
                                onClick: () => { setInspectionPointRedirect(true) } 
                            },
                        ]} />

                    
                    <SingleColumnLayout>

                        <DeviceView
                            tablet={sharedComponent}
                            desktop={sharedComponent} />

                        <Flex flexDirection='column' gap={4}>
                            <H3 color="gray">Valgte lokationer</H3>
                            {locationsWithInspections.length === 0 &&
                                <P>Ingen lokationer tilføjet</P>
                            }
                            <div>
                                {locationsWithInspections.map(l =>
                                {
                                    return (
                                        <LocationPanel
                                            key={l.locationId}
                                            activityId={activity.id}
                                            location={l}
                                            expanded={expandedLocationIds.includes(l.locationId)}
                                            onToggleExpand={handleToggleExpand}
                                            onClickCreate={handleClickCreateInspections}
                                            onUpdate={handleUpdated} />
                                    );
                                })}
                            </div>
                        </Flex>

                        <Flex flexDirection='column' gap={4}>
                            <H3 color="gray">Øvrige lokationer</H3>
                            <Divider />
                            {availableLocations.length === 0 && locationsWithInspections.length === 0 &&
                                <P>Der skal oprettes lokationer for at kunne afvikle sikkerhedsrunderinger</P>
                            }
                            {availableLocations.length === 0 && locationsWithInspections.length > 0 &&
                                <P>Alle lokationer er tilføjet</P>
                            }
                            {availableLocations.map(l =>
                                <ListRow key={l.locationId} styleVariant='outlined'
                                    content={
                                    <Flex justifyContent='stretch'>
                                        <Flex gap={16} alignItems='center'>
                                            <Icon name='facility_domain' size='small' />
                                            <H3>{l.name}</H3>
                                        </Flex>
                                        <Flex justifyContent='flex-end' style={{ flexGrow: 1 }}>
                                            <Button variant='filled' text='Tilføj' size="32px" onClick={() => handleClickCreateInspections(l.locationId)} />
                                        </Flex>
                                    </Flex>
                                    } />
                            )}
                        </Flex>
                    </SingleColumnLayout>
                </>
            }
        </>
    );
}